/**
 * Sync helpers for the `evidence/view.*` envelopes that drive
 * collaborative re-annotation.
 *
 * Operator broadcasts `evidence/view.open {evidence_id}` to pull both
 * sides into the EvidenceAnnotator on the same captured still. Either
 * side can broadcast `evidence/view.close` to dismiss it (UI restricts
 * the "close for both" affordance to the operator; field-side close
 * dismisses locally only).
 *
 * Operator-only initiation is a UI policy — the bus dispatcher accepts
 * either side broadcasting either envelope, so a future "field can
 * propose to share" flow needs no plumbing changes here.
 */
import { useEffect } from "react";

import type { CommandBus } from "./commandBus";
import { useCollabAnnotatorStore } from "./collabAnnotator";

export type ViewOpenPayload = { evidence_id: string };
export type ViewClosePayload = { evidence_id?: string };

export function useEvidenceViewSync(bus: CommandBus | null): void {
  useEffect(() => {
    if (!bus) return;
    const offOpen = bus.on<ViewOpenPayload, void>(
      "evidence/view.open",
      ({ payload }) => {
        if (payload?.evidence_id) {
          useCollabAnnotatorStore.getState().open(payload.evidence_id);
        }
      },
    );
    const offClose = bus.on<ViewClosePayload, void>(
      "evidence/view.close",
      () => {
        useCollabAnnotatorStore.getState().close();
      },
    );
    return () => {
      offOpen();
      offClose();
    };
  }, [bus]);
}


/** Operator-side: open the collaborative annotator for everyone in the
 *  room. Updates the local store immediately and broadcasts so the
 *  field's annotator opens too. */
export function openCollabAnnotator(
  bus: CommandBus | null,
  evidenceId: string,
): void {
  useCollabAnnotatorStore.getState().open(evidenceId);
  if (bus) {
    void bus
      .send({
        topic: "evidence",
        type: "view.open",
        payload: { evidence_id: evidenceId },
        requires_ack: false,
      })
      .catch(() => {});
  }
}